import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarPlus, Camera, Lock } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { downloadIcs, googleCalendarUrl } from "@/lib/calendar";
import { getUploadWindow } from "@/lib/uploadWindow";
import { useCountdown } from "@/lib/useCountdown";
import Petals from "@/components/shared/Petals";

function AddToCalendar({ wedding }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 text-sm backdrop-blur-md bg-white/10 border border-white/25 rounded-full px-4 py-2.5 hover:bg-white/20 transition-colors duration-200"
        data-testid="add-to-calendar-button"
      >
        <CalendarPlus size={16} /> Aggiungi al calendario
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg overflow-hidden z-20 text-neutral-700" data-testid="add-to-calendar-menu">
          <a
            href={googleCalendarUrl(wedding)}
            target="_blank"
            rel="noreferrer"
            className="block px-4 py-3 text-sm hover:bg-neutral-50 transition-colors duration-150"
            data-testid="add-to-calendar-google"
          >
            Google Calendar
          </a>
          <button
            type="button"
            onClick={() => { downloadIcs(wedding); setOpen(false); }}
            className="w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 transition-colors duration-150 border-t"
            style={{ borderColor: "#eee8dd" }}
            data-testid="add-to-calendar-ics"
          >
            Apple / Outlook (.ics)
          </button>
        </div>
      )}
    </div>
  );
}

export default function Hero({ wedding, onOpenUpload }) {
  const countdown = useCountdown(wedding.data_evento);
  const date = new Date(wedding.data_evento);
  const { uploadsOpen, windowOpensAt, windowClosesAt } = getUploadWindow(wedding);

  // The fixed "dal giorno prima" copy was wrong whenever the couple set a
  // custom upload window in Settings -- this spells out the real one, and
  // distinguishes "not open yet" from "already closed" instead of treating
  // both as the same generic locked state.
  const now = Date.now();
  let lockedLabel = "Foto/video dal giorno prima";
  if (!uploadsOpen && windowClosesAt && now > windowClosesAt.getTime()) {
    lockedLabel = "Caricamento foto/video chiuso";
  } else if (!uploadsOpen && windowOpensAt) {
    lockedLabel = `Foto/video si sbloccano il ${windowOpensAt.toLocaleDateString("it-IT", { day: "numeric", month: "long" })} alle ${windowOpensAt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
  }

  // Below `sm` (640px, same breakpoint used everywhere else in the app), a
  // phone loads the smaller variant generated alongside the full upload
  // instead of the same 1920px-wide image a desktop screen gets -- pure CSS
  // (no JS/resize listener) so there's no flash of the wrong image on load.
  const heroMobileUrl = wedding.hero_photo_url_mobile || wedding.hero_photo_url;

  return (
    // Full-screen on phones too: the first thing a guest sees on opening the
    // page should be the photo filling the whole screen, same as desktop.
    // `100svh` (small viewport height) rather than `100vh` -- `vh` is defined
    // by the *largest* possible viewport on mobile Safari/Chrome, so a section
    // sized with it is taller than the visible area the moment the address
    // bar is showing, leaving a sliver of the next section visibly cut in half
    // underneath on first load.
    <section className="relative min-h-[100svh] sm:min-h-[92vh] flex items-end" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center sm:hidden"
        style={{ backgroundImage: `url(${assetUrl(heroMobileUrl)})` }}
      />
      <div
        className="absolute inset-0 bg-cover bg-center hidden sm:block"
        style={{ backgroundImage: `url(${assetUrl(wedding.hero_photo_url)})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10" />
      <Petals />
      <div className="relative z-10 w-full px-6 sm:px-12 pb-16 sm:pb-20 max-w-5xl mx-auto text-white">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="eyebrow !text-white/80 mb-4"
        >
          Ci sposiamo
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-wed text-5xl sm:text-7xl leading-none mb-4"
          data-testid="hero-couple-names"
        >
          {wedding.nome_sposi}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-white/85 text-lg mb-8"
        >
          {date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} · {wedding.location}
        </motion.p>
        {countdown && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="flex gap-3 sm:gap-8 flex-wrap"
            data-testid="hero-countdown"
          >
            {[
              [countdown.d, "Giorni"],
              [countdown.h, "Ore"],
              [countdown.m, "Minuti"],
              [countdown.s, "Secondi"],
            ].map(([v, label]) => (
              <div key={label} className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl px-3 py-3 sm:px-6 sm:py-4 text-center min-w-[64px]">
                <div className="font-wed text-3xl sm:text-4xl">{v}</div>
                <div className="text-[0.65rem] tracking-[0.2em] uppercase text-white/70 mt-1">{label}</div>
              </div>
            ))}
          </motion.div>
        )}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }} className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          {uploadsOpen ? (
            <button
              type="button"
              onClick={onOpenUpload}
              className="upload-cta-attention flex items-center justify-center gap-2.5 text-base sm:text-lg rounded-full px-7 py-4 text-white font-bold shadow-2xl w-fit"
              style={{ background: "var(--wed-primary)" }}
              data-testid="hero-upload-button"
            >
              <Camera size={22} /> Carica foto/video
            </button>
          ) : (
            <span
              className="flex items-center gap-2 text-sm backdrop-blur-md bg-white/10 border border-white/20 rounded-full px-4 py-2.5 text-white/60 w-fit"
              data-testid="hero-upload-locked"
            >
              <Lock size={14} /> {lockedLabel}
            </span>
          )}
          <AddToCalendar wedding={wedding} />
        </motion.div>
      </div>
    </section>
  );
}
